import {
  ReactNode, useEffect, useRef, useState,
} from 'react';
import styled, { css } from 'styled-components';
import { useKeyDown } from '../../../util/useKeyDown';
import { TextInput } from './TextInput';
import { Input } from './Input';
import { OutsideClicker, SearchIcon, useFormConfigurationContext } from '../../../..';
import { Flex } from '../../Flex';

const ClearSearchIconContainer = styled(Flex)<{searchValue: string}>`${({ theme, searchValue }) => `
  background:${theme.colours.neutral[3]};
  width:${theme.padding[4]};
  height:${theme.padding[4]};
  justify-content:center;
  align-items:center;
  border-radius:calc(${theme.padding[4]} / 2);
  cursor: pointer;
  display: ${searchValue ? 'auto' : 'none'}
`}`;

const ClearSearchIcon = styled.i.attrs(() => ({
  className: 'mi-close',
}))` ${({ theme }) => `
    font-size:${theme.fontSize[1]};
    color:${theme.colours.neutral[10]};
`}`;

const SearchContainer = styled.div`${({ theme }) => `
  padding: ${theme.padding[4]};
`}`;

const ScrollListBoxContainer = styled.div<{isTable: boolean}>`${({ theme, isTable }) => `
  position: relative;
  z-index: 2;
  ${isTable ? 'width:100%;' : ''}
  margin-top: ${isTable ? theme.padding[4] : '0'};
`}`;

const ScrollListBox = styled.div`${({ theme }) => `
  position: absolute;
  border:none;
  margin-top:${theme.padding[2]};
  border: ${theme.borderWidth[1]} solid ${theme.colours.neutral[6]};
  border-radius: ${theme.borderRadius[1]};
  background: ${theme.colours.neutral[10]};
  width:100%;
`}`;

const ScrollListScrollBox = styled.div`
  overflow-y: auto;
  max-height:30vh;
`;

const ScrollListBoxItem = styled.div<{isCurrent: boolean, focused: boolean}>`${({ theme, isCurrent, focused }) => `
  padding:${theme.padding[4]};
  padding-top:${theme.padding[1]};
  padding-bottom:${theme.padding[1]};
  font-size:${theme.fontSize[1]};
  cursor: pointer;

  :hover{
    background: ${theme.colours.secondary[6]};
    font-weight: ${theme.fontWeight.normal};
  }
  
  ${isCurrent ? `
    background: ${theme.colours.secondary[4]};
    ` : ''
}

    ${focused ? `
      background: ${theme.colours.secondary[5]};
      font-weight: ${theme.fontWeight.bold};
    ` : ''
}
`}`;

const ScrollListSearchContainerStyles = css`
  padding-bottom:0;
`.toString();

type ListBoxProps = {
  id: string,
  value: string,
  label?: string,
  searchPlaceholder?: string,
  options: { [key: string]: string },
  labelStyles?: string,
  disabled?: boolean,
  onChangeItem: (newValue: string) => void,
  required?: boolean,
  prefix?: ReactNode,
  suffix?: ReactNode,
  onBlur?: (targetValue?: string) => void,
  touchOverride?: boolean,
  errors?: string[],
  warnings?: string[],
  isTable?: boolean,
  isTooltipError?: boolean,
}

export function SearchSelectBox({
  id,
  label,
  searchPlaceholder = 'Search',
  value,
  onChangeItem,
  options,
  labelStyles,
  disabled,
  required,
  prefix,
  suffix,
  onBlur = () => {},
  touchOverride,
  errors = [],
  warnings = [],
  isTable = false,
  isTooltipError = false,
}: ListBoxProps) {
  const [searchValue, setSearchValue] = useState<string>('');
  const [listVisible, setListVisible] = useState<boolean>(false);
  const [dropdownFocused, setDropdownFocused] = useState(false);

  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const [shouldShowError, setShouldShowError] = useState<boolean>(false);
  const inputDropdownRef = useRef<HTMLInputElement>(null);
  const selectContainerRef = useRef<HTMLDivElement>(null);

  const { isDisabled } = useFormConfigurationContext(disabled);

  const showList = () => {
    setListVisible(true);
    setShouldShowError(false);
  };

  const hideList = () => {
    setListVisible(false);
    setSearchFocused(false);
    setShouldShowError(true);
  };

  const toggleList = () => {
    if (listVisible) {
      hideList();
    } else {
      showList();
    }
  };

  const onClickItem = (itemKey: string) => {
    onChangeItem(itemKey ?? '');
    hideList();
  };

  const optionsArray = Object.entries(options).filter(([, optionValue]) => optionValue?.toLowerCase()?.includes(searchValue?.toLowerCase()));

  const listShown = (listVisible && !isDisabled);

  const onSearchFocus = () => {
    setSearchFocused(true);
  };
  const onSearchBlur = () => {
    setSearchFocused(false);
  };

  const [focusedItemIndex, setFocusedItemIndex] = useState<number>(-1);

  useKeyDown(() => {
    if (listShown) {
      setFocusedItemIndex((prev) => (prev === optionsArray.length - 1 ? 0 : prev + 1));
    }
  }, ['ArrowDown'], listShown);

  useKeyDown(() => {
    if (listShown) {
      setFocusedItemIndex((prev) => (prev === 0 ? optionsArray.length - 1 : prev - 1));
    }
  }, ['ArrowUp'], listShown);

  useKeyDown(() => {
    if (listShown && focusedItemIndex >= 0) {
      onClickItem(optionsArray[focusedItemIndex][0]);
    }
  }, ['Enter', ' '], listShown && !searchFocused);

  useKeyDown(() => {
    if (dropdownFocused) {
      toggleList();
    }
  }, ['Enter', ' '], dropdownFocused && !searchFocused);

  useKeyDown(() => {
    if (listShown) {
      inputDropdownRef.current?.focus();
      hideList();
    }
  }, ['Escape'], listShown);

  useKeyDown(() => {
    if (searchFocused) {
      hideList();
    }
  }, ['Tab'], false);

  useEffect(() => {
    if (listShown || searchValue) {
      setFocusedItemIndex(-1);
    }
  }, [listShown, searchValue]);

  const [touched, setTouched] = useState(false);

  const hasError = (touchOverride || touched) && shouldShowError && !!errors?.length;

  const onDropdownBlur = () => {
    setDropdownFocused(false);
    setTouched(true);
    onBlur();
  };

  const onDropdownFocus = () => {
    setDropdownFocused(true);
  };

  useEffect(() => {
    if (listShown && focusedItemIndex >= 0 && selectContainerRef?.current) {
      const element = selectContainerRef?.current.getElementsByClassName('select-options')?.[focusedItemIndex];
      element?.scrollIntoView({ behavior: 'instant' as any, block: 'nearest', inline: 'start' });
    }
  }, [focusedItemIndex]);

  return (
    <OutsideClicker onClickOutside={hideList}>
      <Input
        id={id}
        label={label}
        errors={errors}
        warnings={warnings}
        labelStyles={labelStyles}
        required={required}
        hasError={hasError}
        isTooltipError={isTooltipError}
      >
        <TextInput
          id={`${id}-value`}
          placeholder={searchPlaceholder}
          labelStyles={labelStyles}
          value={options?.[value] ?? ''}
          onChange={(newValue: string) => onChangeItem(newValue)}
          disabled={isDisabled}
          errors={shouldShowError ? errors : []}
          hideErrorMessage
          touchOverride={touchOverride}
          containerStyles={ScrollListSearchContainerStyles}
          prefix={prefix}
          suffix={<div onClick={toggleList}>{suffix || <i className={`mi-chevron-${listShown ? 'up' : 'down'}`} />}</div>}
          readOnly
          onFocus={onDropdownFocus}
          onBlur={onDropdownBlur}
          ref={inputDropdownRef}
          onClick={toggleList}
        />
      </Input>

      {listShown && (
        <ScrollListBoxContainer ref={selectContainerRef} isTable={isTable}>
          <ScrollListBox id={`${id}-list`}>
            <SearchContainer>
              <TextInput
                id={`${id}-search`}
                placeholder="Search"
                value={searchValue}
                onChange={setSearchValue}
                prefix={<SearchIcon />}
                suffix={(
                  <ClearSearchIconContainer
                    onClick={() => setSearchValue('')}
                    searchValue={searchValue}
                  >
                    <ClearSearchIcon />
                  </ClearSearchIconContainer>
                )}
                onFocus={onSearchFocus}
                onBlur={onSearchBlur}
              />
            </SearchContainer>
            <ScrollListScrollBox>
              {optionsArray.map(([optionKey, optionValue], index) => (
                <ScrollListBoxItem
                  className="select-options"
                  key={optionKey}
                  onClick={() => onClickItem(optionKey)}
                  isCurrent={optionKey === value}
                  focused={focusedItemIndex === index}
                >
                  {optionValue}
                </ScrollListBoxItem>
              ))}
            </ScrollListScrollBox>
          </ScrollListBox>
        </ScrollListBoxContainer>
      )}
    </OutsideClicker>
  );
}
